import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { PageHeader, StatusBadge } from "@/components/app/common";
import { DataTable } from "@/components/app/DataTable";
import { useFetch } from "@/hooks/useFetch";
import { fmtDate, inr } from "@/lib/format";
import { CONTRACT_TYPES, LEGACY_CONTRACT_TYPES } from "@/components/order/FamilyFields";
import { BillingAlertsCard } from "@/components/order/BillingAlertsCard";

const COLS = [
  { key: "order_no", label: "LMO no.", mono: true },
  { key: "contract_type", label: "Contract type" },
  { key: "customer", label: "Customer", wrap: true, value: (r) => r.customer_snapshot?.name, render: (r) => r.customer_snapshot?.name },
  { key: "site", label: "Site", wrap: true },
  { key: "contract_start", label: "Start", render: (r) => fmtDate(r.contract_start) },
  { key: "contract_end", label: "End", render: (r) => fmtDate(r.contract_end) },
  { key: "financial_year", label: "FY" },
  { key: "billing_frequency", label: "Billing freq." },
  { key: "billing_mode", label: "Mode" },
  { key: "default_billing_amount", label: "Default amount (₹)", right: true, render: (r) => (r.default_billing_amount == null ? "—" : inr(r.default_billing_amount)) },
  { key: "asset_group_code", label: "Asset Group", mono: true },
  { key: "next_billing_date", label: "Next billing", render: (r) => fmtDate(r.next_billing_date) || "—" },
  { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
];
const sel = "h-9 rounded-md border border-slate-200 bg-white px-2 text-sm";
const opt = (list, empty) => [<option key="" value="">{empty}</option>, ...list.map((x) => <option key={x} value={x}>{x}</option>)];
const iso = (d) => d.toISOString().slice(0, 10);

export default function LmoRegister() {
  const navigate = useNavigate();
  const [f, setF] = useState({ q: "", contract_type: "", status: "", financial_year: "", billing_frequency: "", due_by: "" });
  const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
  const { data } = useFetch(`/lmo-register?${qs}`);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const week = iso(new Date(Date.now() + 7 * 864e5));
  const month = iso(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
  return (
    <div data-testid="lmo-register-page">
      <PageHeader title="LMO Register" subtitle="Long-running contracts: Rental, Operate and AMC / CAMC. Billing schedule and dispatch/challan history live on each LMO. Use Add LMO (top bar) to create one." />
      <div className="mb-3 flex flex-wrap gap-2" data-testid="lmo-filters">
        <Input className="w-56 bg-white" placeholder="LMO, customer, asset group…" value={f.q} onChange={set("q")} data-testid="lmo-search" />
        <select className={sel} value={f.contract_type} onChange={set("contract_type")} data-testid="lmo-filter-type">{opt([...CONTRACT_TYPES, ...LEGACY_CONTRACT_TYPES], "Any contract type")}</select>
        <select className={sel} value={f.status} onChange={set("status")} data-testid="lmo-filter-status">{opt(["draft", "pending_approval", "approved", "in_production", "partially_dispatched", "dispatched", "closed", "cancelled"], "Any status")}</select>
        <Input className="w-36 bg-white" placeholder="FY e.g. 2026-27" value={f.financial_year} onChange={set("financial_year")} data-testid="lmo-filter-fy" />
        <select className={sel} value={f.billing_frequency} onChange={set("billing_frequency")} data-testid="lmo-filter-freq">{opt(["Monthly", "Quarterly", "Half-Yearly", "Yearly", "Custom"], "Any billing frequency")}</select>
        <select className={sel} value={f.due_by} onChange={set("due_by")} data-testid="lmo-filter-due"><option value="">Any next billing</option><option value={week}>Billing due this week</option><option value={month}>Billing due this month</option></select>
      </div>
      <BillingAlertsCard />
      <DataTable testid="lmo-table" columns={COLS} rows={data} onRowClick={(r) => navigate(`/orders/${r.id}`)} exportName="LMO_Register" exportPerm="orders.export" empty="No LMO contracts yet" />
    </div>
  );
}
